import { type Container, type Graphics, Rectangle } from "pixi.js";
import { memo, useCallback, useMemo, useRef } from "react";
import { useConstantScreenSize } from "../hooks/useConstantScreenSize";
import { useScene } from "../hooks/useScene";
import type { PointMapObject } from "../types";
import {
    MEASUREMENT_CULL_RADIUS,
    MEASUREMENT_RADIUS,
    MEASUREMENT_SCALE_LIMITS,
} from "./MeasurementObject";

function usePointPosition(item: PointMapObject) {
    const { coordinates } = useScene();

    return useMemo(
        () => coordinates.worldToScene({ x: item.x, y: item.y }),
        [coordinates, item.x, item.y],
    );
}

const PointObject = ({ item }: { item: PointMapObject }) => {
    const containerRef = useRef<Container | null>(null);
    const pointPixi = usePointPosition(item);
    const cullArea = useMemo(
        () =>
            new Rectangle(
                -MEASUREMENT_CULL_RADIUS,
                -MEASUREMENT_CULL_RADIUS,
                MEASUREMENT_CULL_RADIUS * 2,
                MEASUREMENT_CULL_RADIUS * 2,
            ),
        [],
    );

    useConstantScreenSize(containerRef, MEASUREMENT_SCALE_LIMITS);

    const draw = useCallback(
        (graphics: Graphics) => {
            graphics.clear();
            graphics.circle(0, 0, MEASUREMENT_RADIUS);
            graphics.fill({ color: item.color });
            graphics.stroke({
                color: item.strokeColor ?? item.color,
                width: 1,
            });
        },
        [item.color, item.strokeColor],
    );

    return (
        <pixiContainer
            ref={containerRef}
            x={pointPixi.x}
            y={pointPixi.y}
            label="point-culling-unit"
            cullable
            cullableChildren={false}
            cullArea={cullArea}
            eventMode="none"
        >
            <pixiGraphics eventMode="none" draw={draw} />
        </pixiContainer>
    );
};

export default memo(PointObject, (prev, next) => prev.item === next.item);
